import * as React from 'react';

import { enumPurchaseLoadStatus } from '@rematter/frontend-lib-api-client';
import {
  Footer,
  Grid,
  Row,
  Toggle,
  ToggleButtonGroup,
  Toolbar,
} from '@rematter/ui-web';
import { useAtom } from 'jotai';
import { LucideLayoutGrid, LucideVideo } from 'lucide-react';

import { useTranslations } from '../../../i18n/useTranslations';
import { tradeRoutePaths as tradeRoutes } from '../../../routes';
import { PurchaseLoadRecord } from '../../../types/purchaseLoad';
import { useCreateInboundTicket } from '../hooks/useCreateInboundTicket';
import { useListKeyboardShortcuts } from '../hooks/useListKeyboardShortcuts';
import { useTicketListFilter } from '../hooks/useTicketListFilter';
import { DirectionToggle } from '../shared/DirectionToggle';
import { TicketGridLayout } from '../shared/TicketGridLayout';
import { TicketListFilters } from '../shared/TicketListFilters';

import { InboundTicketListCard } from './InboundTicketListCard';

import {
  $mineOnly,
  $viewMode,
} from '~/modules/trade/_state/scaleTicketPro.state';
import { LayoutViewMode } from '~/modules/trade/types/scaleTickets';
import { useIdentity } from '~/shared/hooks/useIdentity';
import { useModuleLocation } from '~/shared/hooks/useModuleLocation';

//
// ⚛️ ⚛️ ⚛️ INBOUND TICKET GRID ⚛️ ⚛️ ⚛️
//
interface InboundTicketGridProps {
  tickets: PurchaseLoadRecord[];
  onSelectTicket: (id: string) => void;
}

const InboundTicketGrid = ({
  tickets,
  onSelectTicket,
}: InboundTicketGridProps) => (
  <Grid gap4 className="grid-cols-[repeat(auto-fill,minmax(280px,1fr))]">
    {tickets.map((ticket) => (
      <InboundTicketListCard
        key={ticket.id}
        ticket={ticket}
        onSelect={() => onSelectTicket(ticket.id)}
      />
    ))}
  </Grid>
);

//
// ⚛️ ⚛️ ⚛️ VIEW MODE TOGGLE ⚛️ ⚛️ ⚛️
//
interface ViewModeToggleProps {
  viewMode: LayoutViewMode;
  onSelectionChange: (keys: Set<React.Key>) => void;
}

const ViewModeToggle = ({
  viewMode,
  onSelectionChange,
}: ViewModeToggleProps) => (
  <Row gap2 yCenter className="@ViewModeToggle ml-auto">
    <ToggleButtonGroup
      selectionMode="single"
      selectedKeys={new Set([viewMode])}
      onSelectionChange={onSelectionChange}
    >
      <Toggle size="xl" id="grid">
        <LucideLayoutGrid className="h-4 w-4" />
      </Toggle>
      <Toggle size="xl" id="camera">
        <LucideVideo className="h-4 w-4" />
      </Toggle>
    </ToggleButtonGroup>
  </Row>
);

//
// ⚛️ ⚛️ ⚛️ INBOUND TICKET LIST ⚛️ ⚛️ ⚛️
//
export const InboundTicketList = () => {
  const t = useTranslations();
  const [, navigate] = useModuleLocation();

  // Atoms
  const [mineOnly, setMineOnly] = useAtom($mineOnly);
  const [viewMode, setViewMode] = useAtom($viewMode);

  // Data hooks
  const { user } = useIdentity();
  const currentUserId = user?.id;

  const {
    items,
    filter,
    setFilter,
    handleSearchChange,
    handleStatusChange: handleStatusChangeRaw,
    handleStatusToggle,
    handleAccountToggle,
    handleClearAccounts,
    loadingState,
    refetch,
  } = useTicketListFilter({ direction: 'inbound', mineOnly, currentUserId });

  const tickets = items as PurchaseLoadRecord[];

  const { createTicket: handleCreateNew, isCreating } =
    useCreateInboundTicket();

  // Memos
  const hasActiveFilters = React.useMemo(
    () =>
      filter.searchTerm !== '' ||
      (filter.statuses.length === 1 &&
        !filter.statuses.includes(enumPurchaseLoadStatus.Open)) ||
      filter.accountIds.length > 0,
    [filter.searchTerm, filter.statuses, filter.accountIds.length],
  );

  // Callbacks
  const handleClearSearch = React.useCallback(() => {
    setFilter((prev) => ({
      ...prev,
      statuses: [enumPurchaseLoadStatus.Open],
      searchTerm: '',
      accountIds: [],
    }));
  }, [setFilter]);

  const handleSelectTicket = React.useCallback(
    (id: string) => {
      navigate(tradeRoutes.scaleTicketDetail.replace(':id', id));
    },
    [navigate],
  );

  const handleStatusChange = React.useCallback(
    (value?: string) => {
      handleStatusChangeRaw(value);
    },
    [handleStatusChangeRaw],
  );

  const handleViewModeChange = React.useCallback(
    (keys: Set<React.Key>) => {
      const first = [...keys][0];
      if (first) {
        setViewMode(first as LayoutViewMode);
      }
    },
    [setViewMode],
  );

  // Effects
  useListKeyboardShortcuts({
    searchContainerSelector: '.\\@InboundTicketList',
    onCreateNew: handleCreateNew,
    createModifier: 'alt',
  });

  return (
    <div className="@InboundTicketList flex h-full flex-col">
      <Toolbar className="flex-wrap gap-2 border-b px-4 py-2">
        <DirectionToggle />

        <div className="h-6 w-px bg-foreground/10" />

        <TicketListFilters
          layout="list"
          direction="inbound"
          filter={filter}
          onSearchChange={handleSearchChange}
          onStatusChange={handleStatusChange}
          onStatusToggle={handleStatusToggle}
          onAccountToggle={handleAccountToggle}
          onClearAccounts={handleClearAccounts}
          mineOnly={mineOnly}
          onMineOnlyChange={setMineOnly}
          onCreateNew={handleCreateNew}
          isCreating={isCreating}
          onRefresh={() => refetch?.()}
        />

        <ViewModeToggle
          viewMode={viewMode}
          onSelectionChange={handleViewModeChange}
        />
      </Toolbar>

      <TicketGridLayout
        isLoading={!!loadingState?.isInitialLoading}
        ticketsLength={tickets?.length}
        viewMode={viewMode}
        onCreateNew={handleCreateNew}
        isCreating={isCreating}
        hasActiveFilters={hasActiveFilters}
        onClearSearch={handleClearSearch}
      >
        <InboundTicketGrid
          tickets={tickets}
          onSelectTicket={handleSelectTicket}
        />
      </TicketGridLayout>

      <Footer className="border-t px-4 py-2">
        <div className="text-sm text-foreground/50">
          {tickets.length} {t.scaleTicketPro.tickets}
        </div>
      </Footer>
    </div>
  );
};
